import { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

// Traduce los errores de getUserMedia / html5-qrcode (a veces son strings) a un
// mensaje para quien usa el escáner.
export const describeCameraError = (error) => {
  const text = String((error && (error.name || error.message)) || error || '');
  if (/NotAllowed|Permission|denied/i.test(text)) {
    return 'No diste permiso para usar la cámara. Actívalo en los ajustes del navegador para este sitio y pulsa "Activar cámara".';
  }
  if (/NotFound|no camera|Requested device not found/i.test(text)) {
    return 'No se encontró ninguna cámara en este dispositivo. Puedes ingresar el código manualmente.';
  }
  if (/NotReadable|TrackStart|in use/i.test(text)) {
    return 'La cámara está siendo usada por otra aplicación. Ciérrala e inténtalo de nuevo.';
  }
  if (/secure|https|getUserMedia|undefined/i.test(text)) {
    return 'El navegador bloqueó la cámara: se necesita una conexión segura (HTTPS). Puedes ingresar el código manualmente.';
  }
  return 'No se pudo abrir la cámara. Inténtalo de nuevo o ingresa el código manualmente.';
};

// Escaneo directo por cámara trasera con html5-qrcode.
//
// start()/stop() son asíncronos y el efecto de React puede montar/desmontar dos
// veces (StrictMode), así que todas las operaciones se encolan en una sola
// cadena de promesas: nunca hay dos arranques ni un stop() sobre una cámara que
// aún no terminó de abrir.
//
// `onDecode(texto)` se llama una vez por QR leído; la cámara se detiene sola
// después de la primera lectura (quien la usa decide cuándo volver a `start`).
const useQrCamera = (elementId, onDecode) => {
  const [status, setStatus] = useState('idle'); // idle | starting | scanning | error
  const [error, setError] = useState('');
  const instance = useRef(null);
  const queue = useRef(Promise.resolve());
  const alive = useRef(true);
  const decodeRef = useRef(onDecode);
  const handled = useRef(false);

  useEffect(() => { decodeRef.current = onDecode; });

  const enqueue = useCallback((task) => {
    queue.current = queue.current.then(task).catch(() => {});
    return queue.current;
  }, []);

  const stopNow = async () => {
    const scanner = instance.current;
    instance.current = null;
    if (!scanner) return;
    try {
      await scanner.stop();
    } catch (e) {
      // ya estaba detenida
    }
    try {
      scanner.clear();
    } catch (e) {
      // el elemento ya no existe
    }
  };

  const start = useCallback(() => enqueue(async () => {
    if (!alive.current || instance.current) return;
    handled.current = false;
    setError('');
    setStatus('starting');
    const scanner = new Html5Qrcode(elementId, {
      verbose: false,
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
    });
    try {
      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (width, height) => {
            const side = Math.max(120, Math.floor(Math.min(width, height) * 0.7));
            return { width: side, height: side };
          },
        },
        (decodedText) => {
          // la librería repite el callback mientras el QR está a la vista
          if (handled.current) return;
          handled.current = true;
          // se detiene la cámara por la misma cola y luego se avisa
          enqueue(async () => {
            await stopNow();
            if (alive.current) {
              setStatus('idle');
              decodeRef.current(decodedText);
            }
          });
        },
        () => {} // errores de "no hay QR en este cuadro": ruido
      );
      if (!alive.current) {
        instance.current = scanner;
        await stopNow();
        return;
      }
      instance.current = scanner;
      setStatus('scanning');
    } catch (e) {
      try {
        scanner.clear();
      } catch (err) {
        // nada que limpiar
      }
      if (alive.current) {
        setError(describeCameraError(e));
        setStatus('error');
      }
    }
  }), [elementId, enqueue]);

  const stop = useCallback(() => enqueue(async () => {
    await stopNow();
    if (alive.current) setStatus('idle');
  }), [enqueue]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      enqueue(stopNow);
    };
  }, [enqueue]);

  return { status, error, start, stop };
};

export default useQrCamera;
